// stores/vehicleMaintenanceStore.js
'use client';
import { fleetApiGo } from '../api/axiosClient';
import useSWR, { mutate } from 'swr';
import FilterBuilder from '../utils/filterBuilder';
import { assertApiSuccess } from '../utils/response';
import { useMemo } from 'react';

const getTzParam = () => `?tz=${Math.abs(new Date().getTimezoneOffset() / 60)}`;

export const revalidateVehicleMaintenanceList = () => {
  mutate((key) => Array.isArray(key) && key[0] === '/vehicle-maintenance/search-detail');
};

const fetcher = async ([url, payloadStr]) => {
  const payload = JSON.parse(payloadStr);
  const resp = await fleetApiGo.post(url, payload);
  return resp.data;
};

export const WORK_ORDER_SEARCH_FIELDS = {
  vm_number: { name: 'vm_number', label: 'WO Number' },
  license_plate: { name: 'license_plate', label: 'Vehicle' },
  vm_date: { name: 'vm_date', label: 'WO Date' },
  date_completed_actual: { name: 'date_completed_actual', label: 'Actual Date' },
  workshop: { name: 'name', label: 'Workshop', table_name: 'm_workshop' },
  price: { name: 'price', label: 'Price' }
};

const EMPTY_WORK_ORDER_COLUMN = { logic_operator: 'like', name: 'id', operator: 'AND', value: '' };

export const buildWorkOrderSearchPayload = ({
  start = 0,
  length = 25,
  searchKey = 'all',
  searchValue = '',
  statusFilter = '',
  selectedCompanyIds = '',
  companyFilterColumns = []
} = {}) => {
  const fb = new FilterBuilder().setPaging(start, length).setOrder(['id'], false);

  // company filter
  if (Array.isArray(companyFilterColumns) && companyFilterColumns.length > 0) {
    companyFilterColumns.forEach((c) => {
      if (!c?.name) return;
      if (c.logic_operator === 'in') {
        fb.in(c.name, c.value, c.operator || 'AND');
      } else {
        fb.equal(c.name, c.value, c.operator || 'AND');
      }
    });
  } else if (selectedCompanyIds) {
    const s = String(selectedCompanyIds);
    if (s.includes(',')) {
      fb.in('company_id', s);
    } else {
      fb.equal('company_id', s);
    }
  } else {
    fb.filterByCompany();
  }

  // status filter
  if (statusFilter) {
    fb.equal('status', statusFilter, 'AND');
  }

  // search query
  const query = String(searchValue ?? '').trim();
  if (query) {
    if (!searchKey || searchKey === 'all') {
      const seen = new Set();
      Object.values(WORK_ORDER_SEARCH_FIELDS).forEach((f) => {
        const key = `${f.name}-${f.table_name ?? ''}`;
        if (!seen.has(key)) {
          seen.add(key);
          fb.like(f.name, query, 'OR', f.table_name ?? null);
        }
      });
    } else {
      const f = WORK_ORDER_SEARCH_FIELDS[searchKey] || { name: searchKey };
      if (f.name === 'status') {
        fb.equal('status', query, 'AND');
      } else {
        fb.like(f.name, query, 'AND', f.table_name ?? null);
      }
    }
  }

  // joins
  fb.addJoin('company', ['name'])
    .addJoin('workshop', ['name'])
    .addJoin('vm_service_type.company', ['name'])
    .addJoin('vm_service_type.service_type', ['name', 'service_length_unit']);

  const built = fb.build();
  if (!built.columns || built.columns.length === 0) {
    built.columns = [{ ...EMPTY_WORK_ORDER_COLUMN }];
  }

  return built;
};

export const fetchAllVehicleMaintenance = async ({
  searchKey = 'all',
  searchValue = '',
  statusFilter = '',
  companyFilterColumns = [],
  selectedCompanyIds = '',
  total = 0
} = {}) => {
  const limit = 1000;
  let start = 0;
  const allRows = [];

  try {
    while (true) {
      const payload = buildWorkOrderSearchPayload({
        start,
        length: limit,
        searchKey,
        searchValue,
        statusFilter,
        companyFilterColumns,
        selectedCompanyIds
      });

      const raw = await fetcher(['/vehicle-maintenance/search-detail', JSON.stringify(payload)]);
      const candidate = raw?.data ?? raw;
      const nested = candidate?.data ?? candidate;
      const rows = Array.isArray(nested) ? nested : [];

      allRows.push(...rows);

      const totalRecord = Number(candidate?.totalRecord ?? candidate?.totalRecords ?? raw?.totalRecord ?? raw?.totalRecords ?? 0);

      if (rows.length < limit || (totalRecord > 0 && allRows.length >= totalRecord) || (total > 0 && allRows.length >= total)) {
        break;
      }

      start += limit;
    }

    return allRows;
  } catch (error) {
    console.error('fetchAllVehicleMaintenance error:', error);
    return [];
  }
};

export const useVehicleMaintenanceList = (
  page = 0,
  rowsPerPage = 25,
  searchKey = 'all',
  searchValue = '',
  statusFilter = '',
  selectedCompanyIds = '',
  companyFilterColumns = []
) => {
  const start = page * rowsPerPage;

  const payload = useMemo(
    () =>
      buildWorkOrderSearchPayload({
        start,
        length: rowsPerPage,
        searchKey,
        searchValue,
        statusFilter,
        selectedCompanyIds: typeof selectedCompanyIds === 'boolean' ? '' : selectedCompanyIds,
        companyFilterColumns
      }),
    [start, rowsPerPage, searchKey, searchValue, statusFilter, selectedCompanyIds, companyFilterColumns]
  );

  const key = useMemo(() => ['/vehicle-maintenance/search-detail', JSON.stringify(payload)], [payload]);

  const {
    data: raw,
    error,
    isLoading
  } = useSWR(key, fetcher, {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    shouldRetryOnError: false,
    keepPreviousData: true
  });

  const candidate = raw?.data ?? raw;
  const rows = Array.isArray(candidate?.data) ? candidate.data : Array.isArray(candidate) ? candidate : [];
  const totalRecord = Number(candidate?.totalRecord ?? candidate?.totalRecords ?? raw?.totalRecord ?? raw?.totalRecords ?? 0);

  return {
    data: rows,
    totalRecord,
    isLoading,
    error,
    refetch: () => mutate(key)
  };
};

export const getMaintenanceById = async (id) => {
  if (id == null) throw new Error('id is required');
  try {
    const fb = new FilterBuilder().setPaging(0, 1).setOrder(['id'], false).filterByCompany().equal('id', String(id));
    const payload = fb.build();
    const resp = await fleetApiGo.post(`/vehicle-maintenance/search-detail${getTzParam()}`, payload);
    const dataArr = resp?.data?.data ?? resp?.data ?? (Array.isArray(resp) ? resp : null);
    const result = Array.isArray(dataArr) && dataArr.length > 0 ? dataArr[0] : null;
    return result;
  } catch (error) {
    console.error('Failed to fetch maintenance by ID:', error);
    throw error;
  }
};

export const createMaintenance = async (formData = {}) => {
  try {
    const body = { ...formData };
    const resp = await fleetApiGo.post(`/vehicle-maintenance/new${getTzParam()}`, body);
    assertApiSuccess(resp, 'Create maintenance failed');
    revalidateVehicleMaintenanceList();
    return resp.data;
  } catch (error) {
    console.error('Error creating maintenance:', error?.response?.data ?? error);
    throw error;
  }
};

export const updateMaintenance = async (id, formData = {}) => {
  if (id == null) throw new Error('id is required');
  try {
    const body = { ...formData };
    const resp = await fleetApiGo.put(`/vehicle-maintenance/edit/${id}`, body);
    assertApiSuccess(resp, 'Update maintenance failed');
    revalidateVehicleMaintenanceList();
    return resp.data;
  } catch (error) {
    console.error('Error updating maintenance:', error?.response?.data ?? error);
    throw error;
  }
};
